/**
 * [A6] TrialChargeReminderBanner
 *
 * Full-width top banner reminding the clinic that their ₹1 trial is
 * about to convert to a paid charge. Distinct from TrialBanner (S1)
 * which covers feature-flag expiry; this covers the imminent billing event.
 *
 * default  (> 3 days left) — amber — dismissable per 24 h (localStorage)
 * critical (≤ 3 days left) — red   — dismissable per session
 *
 * Figma: [A6] TrialChargeReminderBanner
 */
import React, { useState } from "react";
import { daysLeft } from "../../utils/billing";

interface Props {
    trialEndsAt: string;    // ISO date string
    chargeAmount: string;   // display string e.g. "₹2,199"
    onManage: () => void;
}

const DISMISS_KEY_DEFAULT  = "trial_charge_banner_default_dismissed_at";
const DISMISS_KEY_CRITICAL = "trial_charge_banner_critical_dismissed";
const CRITICAL_DAYS = 3;
const DEFAULT_DISMISS_TTL = 24 * 60 * 60 * 1000; // 24 h

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
}

export const TrialChargeReminderBanner: React.FC<Props> = ({
    trialEndsAt,
    chargeAmount,
    onManage,
}) => {
    const days = daysLeft(trialEndsAt);
    const isCritical = days <= CRITICAL_DAYS;

    const [dismissed, setDismissed] = useState(() => {
        if (isCritical) return !!sessionStorage.getItem(DISMISS_KEY_CRITICAL);
        const stored = localStorage.getItem(DISMISS_KEY_DEFAULT);
        return !!stored && Date.now() - parseInt(stored, 10) < DEFAULT_DISMISS_TTL;
    });

    if (dismissed || days <= 0) return null;

    const dismiss = () => {
        setDismissed(true);
        if (isCritical) {
            sessionStorage.setItem(DISMISS_KEY_CRITICAL, "1");
        } else {
            localStorage.setItem(DISMISS_KEY_DEFAULT, String(Date.now()));
        }
    };

    const message =
        `Your trial ends ${formatDate(trialEndsAt)}. Your card will be charged ${chargeAmount} then. ` +
        `No action needed to continue — or cancel anytime.`;

    return (
        <div
            className={`w-full flex items-center justify-between px-4 sm:px-6 py-2.5 gap-4 ${
                isCritical
                    ? "bg-red-600"
                    : "bg-amber-50 border-b border-amber-200"
            }`}
        >
            <div className="flex items-center gap-2 min-w-0">
                {isCritical ? (
                    <svg className="w-4 h-4 flex-shrink-0 text-white"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                ) : (
                    <svg className="w-4 h-4 flex-shrink-0 text-amber-600"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
                    </svg>
                )}
                <span className={`text-sm font-medium line-clamp-2 ${isCritical ? "text-white" : "text-amber-800"}`}>
                    {message}
                </span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                <button
                    onClick={onManage}
                    className={`text-xs font-semibold px-3 py-2 min-h-[36px] inline-flex items-center rounded-lg transition-colors ${
                        isCritical
                            ? "bg-white text-red-700 hover:bg-red-50"
                            : "bg-amber-700 text-white hover:bg-amber-800"
                    }`}
                >
                    Manage subscription
                </button>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className={`p-2.5 rounded-md hover:bg-black/10 ${isCritical ? "text-white" : "text-amber-700"}`}
                >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default TrialChargeReminderBanner;
